/**
 * @fileoverview Mobile phone prefix stripping and validation.
 * @module utils/mobilePhone
 */

import { logger } from "./logger.js";

const MEXICO_CALLING_CODE = "52";
const NANP_CALLING_CODE = "1";
const FRANCE_CALLING_CODE = "33";

const MEXICO_CALLING_CODE_LENGTH = MEXICO_CALLING_CODE.length;
const NANP_CALLING_CODE_LENGTH = NANP_CALLING_CODE.length;
const FRANCE_CALLING_CODE_LENGTH = FRANCE_CALLING_CODE.length;

/** Digits when a Mexican number includes the 52 calling code. */
const MEXICO_INTERNATIONAL_DIGIT_LENGTH = 12;
/** Digits when a NANP number includes the 1 calling code. */
const NANP_INTERNATIONAL_DIGIT_LENGTH = 11;
/** Digits in a French number with the 33 calling code and 9-digit national number. */
const FRANCE_INTERNATIONAL_DIGIT_LENGTH = 11;

const MX_NANP_NATIONAL_DIGIT_LENGTH = 10;
const FRANCE_NATIONAL_DIGIT_LENGTH = 9;

const MX_NANP_NATIONAL_PATTERN = /^[2-9]\d{9}$/;
const FRANCE_NATIONAL_PATTERN = /^[1-9]\d{8}$/;

/**
 * Resolves a digit string that includes a known calling code.
 * Prefix is checked before length, and both sides of each comparison are the same type.
 * @param {string} digitsOnly
 * @returns {{ national: string, nationalLen: number } | null}
 */
const matchPrefixedMobileNumber = (digitsOnly) => {
  if (
    digitsOnly.startsWith(MEXICO_CALLING_CODE) &&
    digitsOnly.length === MEXICO_INTERNATIONAL_DIGIT_LENGTH
  ) {
    return {
      national: digitsOnly.slice(MEXICO_CALLING_CODE_LENGTH),
      nationalLen: MX_NANP_NATIONAL_DIGIT_LENGTH,
    };
  }

  if (
    digitsOnly.startsWith(NANP_CALLING_CODE) &&
    digitsOnly.length === NANP_INTERNATIONAL_DIGIT_LENGTH
  ) {
    return {
      national: digitsOnly.slice(NANP_CALLING_CODE_LENGTH),
      nationalLen: MX_NANP_NATIONAL_DIGIT_LENGTH,
    };
  }

  if (
    digitsOnly.startsWith(FRANCE_CALLING_CODE) &&
    digitsOnly.length === FRANCE_INTERNATIONAL_DIGIT_LENGTH
  ) {
    return {
      national: digitsOnly.slice(FRANCE_CALLING_CODE_LENGTH),
      nationalLen: FRANCE_NATIONAL_DIGIT_LENGTH,
    };
  }

  return null;
};

/**
 * Strips leading country codes (52, 1, 33) from a mobile number for national display.
 * Aligns with {@link validateMobilePhoneNumber} prefix handling.
 * @param {string} phoneNumber
 * @returns {string}
 */
export const stripMobileCountryPrefix = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return "";
  }

  const digitsOnly = phoneNumber.trim().replace(/\D/g, "");
  return matchPrefixedMobileNumber(digitsOnly)?.national ?? digitsOnly;
};

/**
 * Validates a mobile phone number from Mexico, USA, Canada, or France.
 * Accepts national format or with country code (+52, +1, +33). Spaces, dashes, parentheses and + are ignored.
 * @param {string} phoneNumber
 * @returns {{ isValid: boolean, error: string | null }}
 */
export const validateMobilePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return { isValid: false, error: "Phone number is required" };
  }

  const digitsOnly = phoneNumber.trim().replace(/\D/g, "");
  const prefixed = matchPrefixedMobileNumber(digitsOnly);
  const national = prefixed?.national ?? digitsOnly;
  const nationalLen = prefixed?.nationalLen ?? digitsOnly.length;

  if (!prefixed) {
    logger.debug("Phone prefix unchanged; treating digits as national number.", {
      len: digitsOnly.length,
    });
  }

  if (nationalLen === MX_NANP_NATIONAL_DIGIT_LENGTH) {
    if (!MX_NANP_NATIONAL_PATTERN.test(national)) {
      return {
        isValid: false,
        error:
          "Invalid phone number. Use 10 digits for Mexico or USA/Canada (e.g. 55 1234 5678 or 555 123 4567).",
      };
    }
    return { isValid: true, error: null };
  }

  if (nationalLen === FRANCE_NATIONAL_DIGIT_LENGTH) {
    if (!FRANCE_NATIONAL_PATTERN.test(national)) {
      return {
        isValid: false,
        error:
          "Invalid French phone number. Use 9 digits (e.g. 6 12 34 56 78).",
      };
    }
    return { isValid: true, error: null };
  }

  return {
    isValid: false,
    error:
      "Invalid phone number. Use 10 digits for Mexico/USA/Canada, 9 for France, or include country code (+52, +1, +33).",
  };
};
